import { NavLink, Outlet, useLocation } from "react-router-dom";

import { Tabs, TabsList, TabsTrigger } from "@breakingthelines/design-system";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";

export type SectionTabItem = {
  /** Visible label. */
  label: string;
  /** Destination route. Real navigation - rendered as a link, not a button. */
  to: string;
  /**
   * Whether `pathname` belongs to this tab. Exact-path equality alone is not
   * enough for tabs like Pages, whose route carries a slug
   * (`/pages/:pageSlug`) that does not equal the tab's own default `to`.
   */
  match: (pathname: string) => boolean;
};

type SectionTabsLayoutProps = {
  /** Stable id for this section - namespaces the tab/panel DOM ids. */
  id: string;
  items: SectionTabItem[];
};

function tabId(id: string, item: SectionTabItem): string {
  return `${id}-tab-${item.to.replace(/^\//, "").replace(/\//g, "-")}`;
}

/**
 * Shared chrome for every sidebar section with more than one child route
 * (Moderation, Discovery, Content, Users & Access, Revenue): a tab bar on top
 * plus an `<Outlet/>` for whichever child route matched. The sidebar now
 * links to only one place per section (its default child); switching between
 * the section's own pages happens here instead of via extra sidebar rows.
 *
 * The bar is the design-system's Tabs, but each tab still renders as a router
 * `NavLink`: the page behind a tab is a genuine URL, so the tab has to carry a
 * real `href` that middle-click and open-in-new-tab can use, and it is the
 * link, not the tab's selected state, that moves the router.
 */
export function SectionTabsLayout({ id, items }: SectionTabsLayoutProps) {
  const { pathname } = useLocation();
  const activeItem = items.find((item) => item.match(pathname)) ?? items[0];
  const panelId = `${id}-panel`;

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <Tabs value={activeItem.to} className="shrink-0">
        {/* Every route change goes through a tab's own link, so nothing here
            listens for the selection changing. The arrow keys are the one
            exception: base-ui moves focus along the bar but leaves activation
            to Enter/Space, and these tabs activated on arrow before, so the
            shared handler clicks the link focus landed on. */}
        <TabsList aria-label="Section" onKeyDown={handleTabArrowKeyDown}>
          {items.map((item) => (
            <TabsTrigger
              key={item.to}
              id={tabId(id, item)}
              value={item.to}
              aria-controls={panelId}
              nativeButton={false}
              render={<NavLink to={item.to} />}
            >
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(id, activeItem)}
        className="flex min-w-0 flex-col"
      >
        <Outlet />
      </div>
    </div>
  );
}
